import React from 'react';
import { Tabs } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import ProgramScoringTable from './ProgramScoringTable';
import ScoresPanel from './ScoresPanel';
import MetricsPanel from './MetricsPanel';
import CamOptimizationsPanel from './CamOptimizationsPanel';
import type { ProgramType } from '../../interfaces/CamProgramming.interface';

import './styles.css';

const SCORES_TAB = 'scores';
const METRICS_TAB = 'metrics';
const CAM_OPTIMIZATIONS_TAB = 'cam_optimizations';
const DEFAULT_TAB = 'cam';

const PROGRAM_TABS: { key: ProgramType; label: string }[] = [
	{ key: 'cam', label: 'CAM Programs' },
	{ key: 'keyence', label: 'Keyence Programs' },
	{ key: 'inspection_plan', label: 'Inspection Plans' },
];

const VALID_TAB_KEYS = new Set<string>([
	...PROGRAM_TABS.map(({ key }) => key),
	SCORES_TAB,
	METRICS_TAB,
	CAM_OPTIMIZATIONS_TAB,
]);

const ProgramScoringGridView: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const tabParam = searchParams.get('tab');
	const activeTab =
		tabParam && VALID_TAB_KEYS.has(tabParam) ? tabParam : DEFAULT_TAB;

	// Filter params in the URL belong to the tab that set them (filter user
	// lists differ per program type), so switching tabs starts clean.
	const handleTabChange = (key: string) => {
		setSearchParams(key === DEFAULT_TAB ? {} : { tab: key });
	};

	return (
		<>
			<Header
				title="Program Scoring"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Program Scoring' },
				]}
			/>

			<Tabs
				type="card"
				size="small"
				activeKey={activeTab}
				onChange={handleTabChange}
				tabBarStyle={{ marginBottom: 0 }}
				destroyInactiveTabPane
				items={[
					...PROGRAM_TABS.map(({ key, label }) => ({
						key,
						label,
						children: <ProgramScoringTable programType={key} />,
					})),
					{
						key: SCORES_TAB,
						label: 'Scores',
						children: <ScoresPanel />,
					},
					{
						key: METRICS_TAB,
						label: 'Metrics',
						children: <MetricsPanel />,
					},
					{
						key: CAM_OPTIMIZATIONS_TAB,
						label: 'CAM Optimizations',
						children: <CamOptimizationsPanel />,
					},
				]}
			/>
		</>
	);
};

export default ProgramScoringGridView;
